import { forwardRef, Inject, Injectable } from '@angular/core';
import { BreadcrumbService, BreadCrumbToken } from '../../service/breadcrumb.service';
import { ContextService } from '../../service/context.service';
import { ThingContextService } from '../../service/thing-context.service';
import { LocalizationPipe } from '../../shared/pipe';

export interface FeedbackContext {
    breadcrumb?: string;
    customer?: FeedbackContextEntity;
    location?: FeedbackContextEntity;
    thing?: FeedbackContextThing;
}

export interface FeedbackContextEntity {
    id?: string;
    name?: string;
}

export interface FeedbackContextThing extends FeedbackContextEntity {
    serialNumber?: string;
}

@Injectable()
export class FeedbackContextService {

    private context: FeedbackContext;
    private pageFeedbackUrl: string;

    constructor(
        @Inject(forwardRef(() => BreadcrumbService)) private breadcrumbService: BreadcrumbService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe
    ) { }

    capture(): void {
        this.pageFeedbackUrl = window.location.href;
        const thing = this.thingContextService.getCurrentThing();
        const customer = this.contextService.getCurrentCustomer() || thing?.customer;
        const location = this.contextService.getCurrentLocation() || thing?.location;
        const context: FeedbackContext = {
            breadcrumb: this.getBreadcrumb(),
            customer: customer ? { id: customer.id, name: customer.name } : null,
            location: location ? { id: location.id, name: location.name } : null,
            thing: thing ? { id: thing.id, name: thing.name, serialNumber: thing.serialNumber } : null
        };
        this.context = this.isEmpty(context) ? null : context;
    }

    getContext(): FeedbackContext {
        return this.context;
    }

    getPageFeedbackUrl(): string {
        return this.pageFeedbackUrl;
    }

    reset(): void {
        this.context = null;
        this.pageFeedbackUrl = null;
    }

    private getBreadcrumb(): string {
        const tokens = this.breadcrumbService.getTokens();
        if (!tokens?.length) {
            return null;
        }
        const labels = tokens
            .filter(token => token.name != 'home')
            .map(token => this.getTokenLabel(token))
            .filter(label => !!label);
        return labels.length ? labels.join(' / ') : null;
    }

    private getTokenLabel(token: BreadCrumbToken): string {
        if (token.isLocalized) {
            return this.localizationPipe.transform(token.label);
        }
        return token.label;
    }

    private isEmpty(context: FeedbackContext): boolean {
        return !context.breadcrumb && !context.customer && !context.location && !context.thing;
    }

}